const CODE128_PATTERNS = [
  "212222","222122","222221","121223","121322","131222","122213","122312","132212","221213","221312","231212",
  "112232","122132","122231","113222","123122","123221","223211","221132","221231","213212","223112","312131",
  "311222","321122","321221","312212","322112","322211","212123","212321","232121","111323","131123","131321",
  "112313","132113","132311","211313","231113","231311","112133","112331","132131","113123","113321","133121",
  "313121","211331","231131","213113","213311","213131","311123","311321","331121","312113","312311","332111",
  "314111","221411","431111","111224","111422","121124","121421","141122","141221","112214","112412","122114",
  "122411","142112","142211","241211","221114","413111","241112","134111","111242","121142","121241","114212",
  "124112","124211","411212","421112","421211","212141","214121","412121","111143","111341","131141","114113",
  "114311","411113","411311","113141","114131","311141","411131","211412","211214","211232","2331112",
];

const EAN_L = ["0001101","0011001","0010011","0111101","0100011","0110001","0101111","0111011","0110111","0001011"];
const EAN_G = ["0100111","0110011","0011011","0100001","0011101","0111001","0000101","0010001","0001001","0010111"];
const EAN_R = ["1110010","1100110","1101100","1000010","1011100","1001110","1010000","1000100","1001000","1110100"];
const EAN_PARITY = ["LLLLLL","LLGLGG","LLGGLG","LLGGGL","LGLLGG","LGGLLG","LGGGLL","LGLGLG","LGLGGL","LGGLGL"];

function escapeXml(value) {
  return String(value || "").replace(/[<>&"']/g, (char) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[char]));
}

function code128Bits(value) {
  const text = String(value || "");
  if (!text || !/^[\x20-\x7E]+$/.test(text)) throw new Error("Code 128 requiere caracteres ASCII imprimibles.");
  const values = [...text].map((character) => character.charCodeAt(0) - 32);
  const checksum = (104 + values.reduce((sum, code, index) => sum + code * (index + 1), 0)) % 103;
  return [104, ...values, checksum, 106].map((code) => {
    const widths = CODE128_PATTERNS[code];
    let black = true;
    let bits = "";
    for (const width of widths) {
      bits += (black ? "1" : "0").repeat(Number(width));
      black = !black;
    }
    return bits;
  }).join("");
}

function ean13Bits(value) {
  const text = String(value || "");
  if (!/^\d{13}$/.test(text)) throw new Error("EAN-13 requiere 13 dígitos.");
  const parity = EAN_PARITY[Number(text[0])];
  let bits = "101";
  for (let index = 1; index <= 6; index += 1) bits += parity[index - 1] === "L" ? EAN_L[Number(text[index])] : EAN_G[Number(text[index])];
  bits += "01010";
  for (let index = 7; index <= 12; index += 1) bits += EAN_R[Number(text[index])];
  return `${bits}101`;
}

export function makeBarcodeSvg({ value, type = "code128", productName = "", price = "", showText = true, width = 520, height = 210 }) {
  const bits = type === "ean13" ? ean13Bits(value) : code128Bits(value);
  const quietModules = type === "ean13" ? 11 : 10;
  const totalModules = bits.length + quietModules * 2;
  const moduleWidth = width / totalModules;
  const barTop = productName ? 42 : 18;
  const barHeight = height - barTop - (showText ? 46 : 20);
  const bars = [];
  let start = -1;
  for (let index = 0; index <= bits.length; index += 1) {
    if (bits[index] === "1" && start === -1) start = index;
    if ((bits[index] !== "1" || index === bits.length) && start !== -1) {
      bars.push(`<rect x="${((quietModules + start) * moduleWidth).toFixed(3)}" y="${barTop}" width="${((index - start) * moduleWidth).toFixed(3)}" height="${barHeight}" fill="#17120f"/>`);
      start = -1;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="Código de barras ${escapeXml(value)}">
    <rect width="100%" height="100%" rx="10" fill="#fff"/>
    ${productName ? `<text x="${width / 2}" y="27" text-anchor="middle" font-family="Arial,sans-serif" font-size="18" font-weight="700" fill="#2f211b">${escapeXml(productName)}</text>` : ""}
    ${bars.join("")}
    ${showText ? `<text x="${width / 2}" y="${height - 14}" text-anchor="middle" font-family="Arial,sans-serif" font-size="18" letter-spacing="2" fill="#2f211b">${escapeXml(value)}</text>` : ""}
    ${price ? `<text x="${width - 14}" y="27" text-anchor="end" font-family="Arial,sans-serif" font-size="17" font-weight="700" fill="#8d3f39">${escapeXml(price)}</text>` : ""}
  </svg>`;
}

export function barcodeDataUrl(options) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(makeBarcodeSvg(options))}`;
}

export function downloadBarcodeSvg(options, filename = "etiqueta-kakao.svg") {
  const blob = new Blob([makeBarcodeSvg(options)], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function printBarcodeLabels(options, { copies = 1, labelSize = "50x30" } = {}) {
  const safeCopies = Math.min(100, Math.max(1, Number(copies) || 1));
  const [widthMm, heightMm] = labelSize === "40x25" ? [40, 25] : [50, 30];
  const labels = Array.from({ length: safeCopies }, () => `<div class="label">${makeBarcodeSvg(options)}</div>`).join("");
  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) throw new Error("El navegador bloqueó la vista de impresión.");
  printWindow.document.write(`<!doctype html><html><head><title>Etiquetas Kakao</title><style>
    @page { size: auto; margin: 5mm; }
    * { box-sizing: border-box; }
    body { margin: 0; display: flex; flex-wrap: wrap; gap: 2mm; font-family: Arial, sans-serif; }
    .label { width: ${widthMm}mm; height: ${heightMm}mm; break-inside: avoid; overflow: hidden; }
    .label svg { width: 100%; height: 100%; display: block; }
  </style></head><body>${labels}<script>window.addEventListener('load',()=>setTimeout(()=>window.print(),100));</script></body></html>`);
  printWindow.document.close();
}
